/**
 * Original code copied from lodash at 16/06/2022 https://github.com/lodash/lodash/blob/2da024c3b4f9947a48517639de7560457cd4ec6c/debounce.js#L123
 * Simplified and adapted for our use cases
 * The biggest difference to the implementation of lodash is the feature, that one can now await the result.
 */

export type Debounced<VALUE, RESULT> = {
  debouncedFunc: (value: VALUE) => Promise<RESULT>;
  cancel: () => void;
};

export const debounce = <VALUE, RESULT>(
  func: (value: VALUE) => Promise<RESULT>,
  timeoutMs = 250
): Debounced<VALUE, RESULT> => {
  let lastArg: VALUE | undefined;
  let timerId: NodeJS.Timeout | undefined;
  let lastCallTime: number | undefined;
  let resultPromise: Promise<RESULT>;

  const startTimer = (
    pendingFunc: () => Promise<RESULT>,
    timeout: number
  ): Promise<RESULT> =>
    new Promise((resolve) => {
      timerId = setTimeout(() => {
        resolve(pendingFunc());
      }, timeout);
    });

  const remainingWait = (time: number) => {
    if (lastCallTime === undefined) {
      return 0;
    }
    const timeSinceLastCall = time - lastCallTime;
    return timeoutMs - timeSinceLastCall;
  };

  const invokeFuncIfTimerExpired = (): Promise<RESULT> => {
    const remainingWaitTime = remainingWait(Date.now());
    if (remainingWaitTime <= 0) {
      return invokeFuncIfDebounced();
    } else {
      // Restart the timer.
      resultPromise = startTimer(invokeFuncIfTimerExpired, remainingWaitTime);
      return resultPromise;
    }
  };

  const invokeFuncIfDebounced = (): Promise<RESULT> => {
    timerId = undefined;
    const arg = lastArg;
    lastArg = undefined;

    // Only invoke if we have `lastArg` which means `func` has been debounced at least once.
    if (arg !== undefined) {
      return func(arg);
    } else {
      return resultPromise;
    }
  };

  const cancel = () => {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }
    lastArg = undefined;
    lastCallTime = undefined;
    timerId = undefined;
  };

  const debouncedFunc = async (arg: VALUE): Promise<RESULT> => {
    const time = Date.now();

    lastArg = arg;
    lastCallTime = time;

    if (timerId === undefined) {
      resultPromise = startTimer(invokeFuncIfTimerExpired, timeoutMs);
    }
    // Note: the `await` is needed as `resultPromise` will have changed in the meantime
    await resultPromise;
    return resultPromise;
  };

  return {debouncedFunc, cancel};
};
